import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { auth, APIError } from '../api/client'
import { AuthLayout } from '../components/layout/AuthLayout'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
} from '../components/ui'

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{
    password?: string
    confirmPassword?: string
  }>({})

  if (!token) {
    return (
      <AuthLayout>
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <svg
                className="h-6 w-6 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <CardTitle>Invalid reset link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Link to="/forgot-password">
              <Button fullWidth>Request a new link</Button>
            </Link>
          </CardContent>

          <CardFooter>
            <Link
              to="/login"
              className="font-medium text-[var(--text-primary)] hover:opacity-80 transition-opacity"
            >
              &larr; Back to sign in
            </Link>
          </CardFooter>
        </Card>
      </AuthLayout>
    )
  }

  const validateForm = (): boolean => {
    const errors: typeof fieldErrors = {}

    if (!password) {
      errors.password = 'Password is required'
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters'
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match'
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      await auth.resetPassword(token, password)
      navigate('/login?message=password_reset')
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Reset your password</CardTitle>
          <CardDescription>Enter a new password for your account</CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Input
              label="New password"
              type="password"
              name="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setFieldErrors((prev) => ({ ...prev, password: undefined }))
              }}
              placeholder="Enter new password"
              required
              autoComplete="new-password"
              autoFocus
              error={fieldErrors.password}
              hint={!fieldErrors.password ? 'At least 8 characters' : undefined}
            />

            <Input
              label="Confirm new password"
              type="password"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
                setFieldErrors((prev) => ({ ...prev, confirmPassword: undefined }))
              }}
              placeholder="Confirm new password"
              required
              autoComplete="new-password"
              error={fieldErrors.confirmPassword}
            />

            <Button type="submit" fullWidth loading={loading}>
              Reset password
            </Button>
          </form>
        </CardContent>

        <CardFooter>
          <Link
            to="/login"
            className="font-medium text-[var(--text-primary)] hover:opacity-80 transition-opacity"
          >
            &larr; Back to sign in
          </Link>
        </CardFooter>
      </Card>
    </AuthLayout>
  )
}
